import * as Sentry from '@sentry/react';
import { publisherJoined, videoroomError } from 'features/streaming/actions';
import { selectLocalAudioBroadcasting } from 'features/streaming/streamingSlice';
import { JanusJS } from 'lib/janus';
import { SignalingSocket } from 'services/signaling';
import { store } from 'store/store';
import { logger } from 'utils/logger';
import { GENERIC_JANUS_ERROR } from 'utils/webrtc/errors';
import { RTCClient } from 'utils/webrtc';
import { BasePublishing } from 'utils/webrtc/publishing';
import { selectAudioSettings } from 'features/user-media/userMediaSlice';
import { publisherOpusParams } from 'utils/webrtc/publishing/publisherOpusParams';
import { setOpusFmtpParams } from 'utils/webrtc/publishing/setOpusFmtpParams';
import {
  emitPublishEvent,
  PUBLISH_AUDIO_HOT_MIC_EVENT,
  PUBLISH_AUDIO_HOT_MIC_MESSAGE,
} from 'utils/webrtc/publishing/publishTelemetry';

export class MessageHandler {
  feed: BasePublishing;

  constructor(feed: BasePublishing) {
    this.feed = feed;
  }

  onMessage = (message: JanusJS.EvtMessage, jsep?: JanusJS.JSEP) => {
    const ignoredLogEvents = ['talking', 'stopped-talking'];
    const feed = this.feed.handle!;

    if (jsep) {
      const { quality } = selectAudioSettings(store.getState());

      feed.videoroom.handleRemoteJsep({
        customizeSdp: (remoteJsep) => {
          remoteJsep.sdp = setOpusFmtpParams(
            remoteJsep.sdp,
            publisherOpusParams({ quality, kind: this.feed.kind })
          );
        },
        jsep,
        error: GENERIC_JANUS_ERROR(`publish_local_feed_${feed.handle}`),
      });
    }

    const event = message.videoroom;

    if (!ignoredLogEvents.includes(event!)) {
      logger.remote({ action: true }).debug(`Got local message in publishing feed:`, message);
    }

    switch (event) {
      case 'joined': {
        store.dispatch(publisherJoined({ handle: feed.handle, message, kind: this.feed.kind }));
        break;
      }
      case 'event': {
        if (message.publishers) {
          break;
        }
        if (message.unpublished) {
          if (message.unpublished === 'ok') {
            this.feed.cleanupConnection();
          } else {
            RTCClient.receivingFeed.unsubscribe(message.unpublished);
          }
        } else if (message.leaving) {
          RTCClient.receivingFeed.unsubscribe(message.leaving);
        } else if (message.configured === 'ok') {
          logger.debug(`Publisher configured`);
        } else if (message.error_code) {
          store.dispatch(
            videoroomError({
              handle: feed.handle,
              message: { error_code: message.error_code, error: String(message.error ?? '') },
              source: 'publisher',
            })
          );
        } else {
          const messageStr = 'Got an anomalous message in publishing feed';

          logger.remote({ system: true, capture: 'streaming' }).warn(messageStr, message);
        }
        break;
      }
      case 'talking': {
        const { handle } = this.feed;
        if (handle && handle.feedId === message.id) {
          const broadcasting = selectLocalAudioBroadcasting(store.getState());
          const audioMid = Object.values(handle.streams).find(
            (stream) => stream.type === 'audio'
          )?.mid;
          // Only a provably live sender track while the UI shows muted is a real hot-mic. An SFU 'talking'
          // on an already-muted track (stale event, in-flight renegotiation, or selector lag) is not a
          // hot-mic and must not alert (MONZA-28R).
          if (!broadcasting && audioMid && !handle.videoroom.isAudioMuted(audioMid)) {
            // One message string on both surfaces (the Sentry issue and the telemetry event) —
            // feedId is unbounded and belongs in attributes, not text.
            Sentry.captureException(new Error(PUBLISH_AUDIO_HOT_MIC_MESSAGE));
            emitPublishEvent({
              event: PUBLISH_AUDIO_HOT_MIC_EVENT,
              message: PUBLISH_AUDIO_HOT_MIC_MESSAGE,
              level: 'error',
              feedType: this.feed.kind,
              visibility: 'fleet',
              cap: 3,
              attributes: { feedId: handle.feedId, audioMid },
            });
          }
          SignalingSocket.send({ event: 'startedTalking' });
        }
        break;
      }
      case 'stopped-talking':
        if (this.feed.handle?.feedId === message.id) {
          SignalingSocket.send({ event: 'stoppedTalking' });
        }
        break;
      default:
        logger
          .remote({ system: true, capture: 'streaming' })
          .warn('Got an anomalous message in publishing feed', message);
    }
  };
}
